import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, CheckCircle2, Circle, Award, Play, Plus, UserRound } from 'lucide-react';
import { useMember, formatApiError, hasTier } from '../../access/MemberAuthContext';
import { downloadCertificate } from './certificatePdf';

const API = process.env.REACT_APP_BACKEND_URL;
const opts = { withCredentials: true };

const ModuleList = ({ programme, progress }) => (
  <ol className="ac-modules" data-testid={`ac-modules-${programme.code}`}>
    {programme.modules.map((m) => {
      const done = progress?.[m.code]?.completed;
      return (
        <li key={m.code} className={`ac-module ${m.available ? '' : 'is-planned'}`} data-testid={`ac-module-${m.code}`}>
          <span className="ac-module-code">{m.code}</span>
          <div>
            <h4>{m.title}</h4>
            <p>{m.summary}</p>
            <span className="ac-module-badge"><Award size={13} aria-hidden="true" /> {m.achievement}</span>
          </div>
          <span className="ac-module-state">
            {done ? <><CheckCircle2 size={16} aria-hidden="true" /> Completed</> : m.available ? <><Circle size={16} aria-hidden="true" /> Available</> : <><Lock size={14} aria-hidden="true" /> In development</>}
          </span>
        </li>
      );
    })}
  </ol>
);

const AddChild = ({ onAdded }) => {
  const [name, setName] = useState('');
  const [age, setAge] = useState('5-7');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/academy/children`, { first_name: name, age_band: age }, opts);
      setName(''); onAdded(data); toast.success(`${data.first_name} added.`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <form className="ac-addchild" onSubmit={submit} data-testid="ac-add-child-form">
      <label>Child&rsquo;s first name or nickname<input required maxLength={40} value={name} onChange={(e) => setName(e.target.value)} data-testid="ac-child-name" /></label>
      <label>Age band<select value={age} onChange={(e) => setAge(e.target.value)} data-testid="ac-child-age">
        <option value="5-7">5–7</option><option value="8-11">8–11</option><option value="12-16">12–16</option><option value="16-18">16–18</option>
      </select></label>
      <button type="submit" className="conv-btn-ghost" disabled={busy} data-testid="ac-child-add"><Plus size={16} aria-hidden="true" /> Add child</button>
    </form>
  );
};

const ChildRow = ({ child, programme, enrolment, onEnrol }) => {
  const next = programme.modules.find((m) => m.available && !enrolment?.progress?.[m.code]?.completed) || programme.modules.find((m) => m.available);
  const started = next && enrolment?.progress?.[next.code];
  return (
    <li className="ac-child" data-testid={`ac-child-${child.id}`}>
      <span className="ac-child-name"><UserRound size={16} aria-hidden="true" /> {child.first_name} <small>({child.age_band})</small></span>
      {!enrolment && (
        <button type="button" className="conv-btn-primary" onClick={() => onEnrol(child.id)} data-testid={`ac-enrol-${child.id}`}>Enrol in {programme.title}</button>
      )}
      {enrolment && (
        <>
          <span className="ac-child-progress" data-testid={`ac-progress-${child.id}`}>
            {programme.modules.filter((m) => enrolment.progress?.[m.code]?.completed).length} of {programme.modules.length} modules complete
            {enrolment.achievements?.length > 0 && <> · Badges: {enrolment.achievements.map((a) => a.title).join(', ')}</>}
          </span>
          {enrolment.certificate && (
            <button type="button" className="conv-btn-ghost" onClick={() => downloadCertificate({ childName: child.first_name, certificate: enrolment.certificate.title, programme: `${programme.code} ${programme.title}`, issuedAt: enrolment.certificate.issuedAt }).catch(() => toast.error('Could not create the certificate.'))} data-testid={`ac-certificate-${child.id}`}>
              <Award size={16} aria-hidden="true" /> Download certificate
            </button>
          )}
          {next && (
            <Link to={`/academy/learn/${programme.code}/${next.code}?child=${child.id}`} className="conv-btn-primary" data-testid={`ac-start-${child.id}`}>
              <Play size={16} aria-hidden="true" /> {enrolment.progress?.[next.code]?.completed ? `Replay ${next.code}` : started ? `Resume ${next.code}` : `Start ${next.code}`}
            </Link>
          )}
        </>
      )}
    </li>
  );
};

const FamilyPanel = ({ programme }) => {
  const { member } = useMember();
  const [children, setChildren] = useState([]);
  const [enrolments, setEnrolments] = useState([]);
  const load = useCallback(async () => {
    const [c, e] = await Promise.all([axios.get(`${API}/api/academy/children`, opts), axios.get(`${API}/api/academy/enrolments`, opts)]);
    setChildren(c.data); setEnrolments(e.data);
  }, []);
  const eligible = member && hasTier(member, 'ACCESS_PLUS');
  useEffect(() => { if (eligible) load().catch(() => toast.error('Could not load your family profiles.')); }, [eligible, load]);

  if (member === null) return null;
  if (!member) {
    return <p className="ac-gate" data-testid="ac-gate-signin"><Link to={`/access/sign-in?next=/academy/${programme.pathway}`}>Sign in</Link> with your N3URO Access account to enrol your child. Enrolment is for Access+ members and above.</p>;
  }
  if (!eligible) {
    return <p className="ac-gate" data-testid="ac-gate-upgrade">Academy enrolment is included with <strong>Access+</strong> and above. <Link to="/access#tiers">See membership options</Link>.</p>;
  }
  const enrol = async (childId) => {
    try {
      const { data } = await axios.post(`${API}/api/academy/enrolments`, { child_id: childId, programme: programme.code }, opts);
      setEnrolments((p) => [...p.filter((x) => !(x.childId === childId && x.programme === programme.code)), data]);
      toast.success('Enrolled. Ready when you are.');
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="ac-family" data-testid="ac-family-panel">
      <h3>Your family</h3>
      {children.length === 0 && <p className="np-help">Add a child to get started. We only ask for a first name or nickname and an age band.</p>}
      <ul className="ac-children">
        {children.map((c) => (
          <ChildRow key={c.id} child={c} programme={programme} enrolment={enrolments.find((e) => e.childId === c.id && e.programme === programme.code)} onEnrol={enrol} />
        ))}
      </ul>
      <AddChild onAdded={(c) => setChildren((p) => [...p, c])} />
    </div>
  );
};

const AcademyProgramme = ({ code }) => {
  const [programme, setProgramme] = useState(null);
  useEffect(() => { axios.get(`${API}/api/academy/programmes/${code}`).then((r) => setProgramme(r.data)).catch(() => setProgramme(false)); }, [code]);
  if (programme === false) return <p className="kaz-error" role="alert">We could not load the programme. Please refresh.</p>;
  if (!programme) return <p className="kaz-loading" role="status">Loading programme…</p>;
  return (
    <section className="ac-programme" aria-labelledby={`ac-${code}-h`} data-testid={`ac-programme-${code}`}>
      <span className="subpage-eyebrow">{programme.code} · {programme.level} {programme.ages}</span>
      <h2 id={`ac-${code}-h`} className="np-h2">{programme.title}</h2>
      <p className="subpage-lede">{programme.summary} Lessons are calm and predictable, with optional read-aloud, no timers and as many tries as needed. Complete all six modules to earn the <strong>{programme.certificate}</strong> certificate.</p>
      <ModuleList programme={programme} />
      <FamilyPanel programme={programme} />
    </section>
  );
};

export default AcademyProgramme;
